import { useEffect, useState } from "react";
import { Plus, Trash2, Calendar, MapPin, Clock, Pencil } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../api";
import { Card, PageHeader, EmptyState, formatDate } from "../ui";
import { DateInput, todayISO } from "../DateInput";

export default function AdminEventos() {
  const [events, setEvents] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({
    title: "",
    description: "",
    event_date: "",
    event_time: "",
    location: "",
  });
  const [busy, setBusy] = useState(false);

  const load = () =>
    api.get("/portal/events").then((r) => setEvents(r.data)).catch(() => {});

  useEffect(() => {
    load();
  }, []);

  const resetForm = () => {
    setEditing(null);
    setForm({
      title: "",
      description: "",
      event_date: "",
      event_time: "",
      location: "",
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (editing) {
        await api.patch(`/admin/events/${editing.id}`, form);
        toast.success("Evento atualizado");
      } else {
        await api.post("/admin/events", form);
        toast.success("Evento criado");
      }
      setOpen(false);
      resetForm();
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Excluir este evento do calendário?")) return;
    try {
      await api.delete(`/admin/events/${id}`);
      toast.success("Evento excluído");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const edit = (ev) => {
    setEditing(ev);
    setForm({
      title: ev.title || "",
      description: ev.description || "",
      event_date: ev.event_date || "",
      event_time: ev.event_time || "",
      location: ev.location || "",
    });
    setOpen(true);
  };

  const today = todayISO();
  const upcoming = (events || []).filter((e) => e.event_date >= today);
  const past = (events || []).filter((e) => e.event_date < today);

  return (
    <div data-testid="admin-eventos">
      <PageHeader
        title="Calendário de Eventos"
        subtitle="Reuniões, assembleias e atividades da ABLEMP — visíveis no painel de todos os associados."
        actions={
          <button
            data-testid="new-event-btn"
            onClick={() => {
              resetForm();
              setOpen((v) => !v);
            }}
            className="inline-flex items-center gap-2 bg-[#0A0F14] text-white px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776]"
          >
            <Plus size={14} /> Novo evento
          </button>
        }
      />

      {open && (
        <Card className="p-5 mb-5">
          <form data-testid="event-form" onSubmit={submit} className="grid md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label>Título *</Label>
              <input
                data-testid="event-title"
                required
                minLength={2}
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm"
              />
            </div>
            <div>
              <Label>Data *</Label>
              <DateInput
                testid="event-date"
                required
                value={form.event_date}
                onChange={(v) => setForm({ ...form, event_date: v })}
              />
            </div>
            <div>
              <Label>Horário</Label>
              <input
                data-testid="event-time"
                type="time"
                value={form.event_time}
                onChange={(e) =>
                  setForm({ ...form, event_time: e.target.value })
                }
                className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Local</Label>
              <input
                data-testid="event-location"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                placeholder="Ex.: Sede ABLEMP / Online via Zoom"
                className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Descrição</Label>
              <textarea
                data-testid="event-description"
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                className="mt-1 w-full border border-slate-300 px-3 py-2.5 rounded-sm min-h-[100px]"
              />
            </div>
            <div className="md:col-span-2 flex gap-2">
              <button
                data-testid="event-submit"
                disabled={busy}
                className="px-5 py-2.5 bg-[#0A0F14] text-white text-[11px] uppercase tracking-[0.22em] hover:bg-[#002776] disabled:opacity-60"
              >
                {editing ? "Atualizar evento" : "Salvar evento"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  resetForm();
                }}
                className="px-5 py-2.5 border border-slate-300 text-[11px] uppercase tracking-[0.22em] hover:border-slate-400"
              >
                Cancelar
              </button>
            </div>
          </form>
        </Card>
      )}

      {events === null ? (
        <div className="text-sm text-slate-500">Carregando...</div>
      ) : events.length === 0 ? (
        <EmptyState
          title="Nenhum evento ainda"
          hint="Cadastre o próximo evento da ABLEMP. Ele aparecerá automaticamente no painel dos associados."
        />
      ) : (
        <div className="grid lg:grid-cols-2 gap-5">
          <EventColumn
            label="Próximos eventos"
            items={upcoming}
            edit={edit}
            remove={remove}
            empty="Sem eventos futuros."
            accent="#009B3A"
          />
          <EventColumn
            label="Eventos passados"
            items={past}
            edit={edit}
            remove={remove}
            empty="Sem eventos passados."
            accent="#475569"
          />
        </div>
      )}
    </div>
  );
}

const Label = ({ children }) => (
  <label className="text-[11px] uppercase tracking-[0.22em] text-slate-500">
    {children}
  </label>
);

const EventColumn = ({ label, items, edit, remove, empty, accent }) => (
  <Card className="p-5">
    <div className="flex items-center justify-between mb-3">
      <h3 className="font-serif-display text-xl">{label}</h3>
      <span className="text-[10px] uppercase tracking-[0.22em] text-slate-500">
        {items.length} {items.length === 1 ? "evento" : "eventos"}
      </span>
    </div>
    {items.length === 0 ? (
      <div className="text-sm text-slate-500 py-6 text-center border border-dashed border-slate-200 rounded-sm">
        {empty}
      </div>
    ) : (
      <ul className="space-y-3">
        {items.map((ev) => (
          <li
            key={ev.id}
            data-testid={`event-row-${ev.id}`}
            className="flex items-start gap-3 p-3 border border-slate-200 rounded-sm hover:bg-slate-50"
          >
            <div
              className="h-12 w-12 grid place-items-center rounded-sm shrink-0"
              style={{ background: accent + "18", color: accent }}
            >
              <Calendar size={18} strokeWidth={1.4} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-serif-display text-lg leading-tight">
                {ev.title}
              </div>
              <div className="mt-1 text-xs text-slate-500 flex flex-wrap items-center gap-3">
                <span className="inline-flex items-center gap-1">
                  <Calendar size={12} /> {formatDate(ev.event_date)}
                </span>
                {ev.event_time && (
                  <span className="inline-flex items-center gap-1">
                    <Clock size={12} /> {ev.event_time}
                  </span>
                )}
                {ev.location && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin size={12} /> {ev.location}
                  </span>
                )}
              </div>
              {ev.description && (
                <p className="mt-1.5 text-sm text-slate-600 line-clamp-2">
                  {ev.description}
                </p>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <button
                data-testid={`edit-event-${ev.id}`}
                onClick={() => edit(ev)}
                className="p-1.5 border border-slate-300 hover:border-[#002776]"
                title="Editar"
              >
                <Pencil size={12} />
              </button>
              <button
                data-testid={`delete-event-${ev.id}`}
                onClick={() => remove(ev.id)}
                className="p-1.5 border border-rose-300 text-rose-700 hover:bg-rose-50"
                title="Excluir"
              >
                <Trash2 size={12} />
              </button>
            </div>
          </li>
        ))}
      </ul>
    )}
  </Card>
);
